/**
 * ProvisionModal - Configure and start store provisioning
 */

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface Preset {
  id: string;
  name: string;
  description: string;
  colors: Record<string, string>;
  collections: string[];
  keywords: string[];
}

interface ProvisionModalProps {
  store: {
    id: string;
    name: string;
    shopify_domain: string;
  };
  onClose: () => void;
  onStart: (job: { id: string }) => void;
}

// Available gradient presets by category
const GRADIENT_PRESETS = {
  minimal: ['minimal_white', 'minimal_gray', 'warm_cream', 'cool_blue', 'soft_pink', 'mint_fresh'],
  bold: ['sunset', 'ocean', 'forest', 'fire', 'purple_haze', 'midnight'],
  solid: ['pure_white', 'pure_black', 'navy', 'forest_green', 'burgundy'],
  premium: ['gold_gradient', 'silver_gradient', 'rose_gold']
};

const NICHE_DEFAULT_PRESETS: Record<string, { hero: string; collection: string }> = {
  tech: { hero: 'midnight', collection: 'ocean' },
  home: { hero: 'warm_cream', collection: 'minimal_gray' },
  fitness: { hero: 'fire', collection: 'sunset' },
  beauty: { hero: 'rose_gold', collection: 'soft_pink' },
  pets: { hero: 'forest', collection: 'mint_fresh' },
  outdoor: { hero: 'forest', collection: 'ocean' },
  default: { hero: 'minimal_white', collection: 'minimal_gray' }
};

const LOGO_STYLES = [
  { id: 'modern', name: 'Modern', desc: 'Bold uppercase' },
  { id: 'elegant', name: 'Elegant', desc: 'Serif letterhead' },
  { id: 'minimal', name: 'Minimal', desc: 'Clean lowercase' },
  { id: 'playful', name: 'Playful', desc: 'Fun & casual' }
];

export function ProvisionModal({ store, onClose, onStart }: ProvisionModalProps) {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [selectedNiche, setSelectedNiche] = useState('');
  const [brandName, setBrandName] = useState(store.name);
  const [tagline, setTagline] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPresets, setLoadingPresets] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ollamaStatus, setOllamaStatus] = useState<{ healthy: boolean; models?: string[] } | null>(null);

  // Image generation options
  const [generateImages, setGenerateImages] = useState(true);
  const [heroPreset, setHeroPreset] = useState('');
  const [collectionPreset, setCollectionPreset] = useState('');
  const [logoStyle, setLogoStyle] = useState('modern');
  const [showAdvancedImages, setShowAdvancedImages] = useState(false);

  // Update image presets when niche changes
  const handleNicheSelect = (nicheId: string) => {
    setSelectedNiche(nicheId);
    const defaults = NICHE_DEFAULT_PRESETS[nicheId] || NICHE_DEFAULT_PRESETS.default;
    setHeroPreset(defaults.hero);
    setCollectionPreset(defaults.collection);
  };

  useEffect(() => {
    fetchPresets();
    checkOllamaHealth();
  }, []);

  const fetchPresets = async () => {
    try {
      const res = await fetch('/api/autostore/provision/presets');
      const data = await res.json();
      setPresets(data.presets || []);
    } catch (err) {
      setError('Failed to load presets');
    } finally {
      setLoadingPresets(false);
    }
  };

  const checkOllamaHealth = async () => {
    try {
      const res = await fetch('/api/autostore/provision/health');
      const data = await res.json();
      setOllamaStatus(data.ollama);
    } catch (err) {
      setOllamaStatus({ healthy: false });
    }
  };

  const handleStart = async () => {
    if (!selectedNiche) {
      setError('Please select a niche');
      return;
    }

    if (!ollamaStatus?.healthy) {
      setError('Ollama is not available. Make sure the Windows workstation is connected.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/autostore/provision/${store.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          niche: selectedNiche,
          brandName: brandName || undefined,
          tagline: tagline || undefined,
          imageOptions: generateImages ? {
            enabled: true,
            heroPreset: heroPreset || undefined,
            collectionPreset: collectionPreset || undefined,
            logoStyle: logoStyle
          } : { enabled: false }
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error);
      }

      onStart({ id: data.jobId });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start provisioning');
      setLoading(false);
    }
  };

  const selectedPreset = presets.find(p => p.id === selectedNiche);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Provision Store</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-xl">&times;</button>
        </div>

        <p className="text-sm text-zinc-400 mb-4">
          Auto-configure <span className="text-white">{store.name}</span> with professional theme, pages, and content using AI.
        </p>

        {/* Ollama Status */}
        <div className="mb-4 p-3 bg-zinc-700/50 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${ollamaStatus?.healthy ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-sm">
              {ollamaStatus?.healthy ? 'Ollama Ready' : 'Ollama Unavailable'}
            </span>
          </div>
          {ollamaStatus?.models && (
            <span className="text-xs text-zinc-500">
              {ollamaStatus.models.length} models available
            </span>
          )}
        </div>

        {error && (
          <div className="p-3 mb-4 bg-red-900/50 rounded text-red-200 text-sm">
            {error}
          </div>
        )}

        {/* Niche Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Select Store Niche</label>
          {loadingPresets ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {presets.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => handleNicheSelect(preset.id)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    selectedNiche === preset.id
                      ? 'border-purple-500 bg-purple-500/20'
                      : 'border-zinc-600 hover:border-zinc-500'
                  }`}
                >
                  <div className="font-medium text-sm">{preset.name}</div>
                  <div className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{preset.description}</div>
                  <div className="flex gap-1 mt-2">
                    {Object.values(preset.colors).slice(0, 4).map((color, i) => (
                      <div
                        key={i}
                        className="w-4 h-4 rounded-full border border-zinc-600"
                        style={{ backgroundColor: color as string }}
                      />
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected Preset Preview */}
        {selectedPreset && (
          <div className="mb-4 p-3 bg-zinc-700/50 rounded-lg">
            <h4 className="font-medium text-sm mb-2">Collections that will be created:</h4>
            <div className="flex flex-wrap gap-1">
              {selectedPreset.collections.map(c => (
                <span key={c} className="px-2 py-0.5 text-xs bg-zinc-600 rounded">{c}</span>
              ))}
            </div>
            <h4 className="font-medium text-sm mt-3 mb-1">Keywords:</h4>
            <p className="text-xs text-zinc-400">{selectedPreset.keywords.join(', ')}</p>
          </div>
        )}

        {/* Brand Override */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Brand Name</label>
          <input
            type="text"
            value={brandName}
            onChange={e => setBrandName(e.target.value)}
            placeholder="Leave blank to auto-generate"
            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-sm"
          />
          <p className="text-xs text-zinc-500 mt-1">
            Clear this field to have AI generate a unique brand name
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-1">Tagline (optional)</label>
          <input
            type="text"
            value={tagline}
            onChange={e => setTagline(e.target.value)}
            placeholder="Leave blank to auto-generate"
            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-sm"
          />
        </div>

        {/* Image Generation Options */}
        <div className="mb-6 p-4 bg-zinc-700/30 rounded-lg border border-zinc-600">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="generateImages"
                checked={generateImages}
                onChange={e => setGenerateImages(e.target.checked)}
                className="w-4 h-4 rounded bg-zinc-700 border-zinc-600"
              />
              <label htmlFor="generateImages" className="text-sm font-medium flex items-center gap-2">
                Generate Store Images
                <span className="px-1.5 py-0.5 text-xs bg-cyan-500/20 text-cyan-400 rounded">Programmatic</span>
              </label>
            </div>
            {generateImages && (
              <button
                type="button"
                onClick={() => setShowAdvancedImages(!showAdvancedImages)}
                className="text-xs text-zinc-400 hover:text-white"
              >
                {showAdvancedImages ? 'Hide Options' : 'Customize'}
              </button>
            )}
          </div>

          {generateImages && (
            <div className="text-xs text-zinc-400 mb-3">
              Hero background, logo, and collection banners using gradient presets
            </div>
          )}

          {generateImages && showAdvancedImages && (
            <div className="space-y-4 pt-3 border-t border-zinc-600">
              {/* Logo Style */}
              <div>
                <label className="block text-xs font-medium mb-2 text-zinc-300">Logo Style</label>
                <div className="flex gap-2">
                  {LOGO_STYLES.map(style => (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => setLogoStyle(style.id)}
                      className={`px-3 py-2 rounded text-xs transition-all ${
                        logoStyle === style.id
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40'
                          : 'bg-zinc-700 text-zinc-400 border border-zinc-600 hover:border-zinc-500'
                      }`}
                    >
                      <div className="font-medium">{style.name}</div>
                      <div className="text-[10px] opacity-70">{style.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Hero Background Preset */}
              <div>
                <label className="block text-xs font-medium mb-2 text-zinc-300">Hero Background</label>
                <select
                  value={heroPreset}
                  onChange={e => setHeroPreset(e.target.value)}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-sm"
                >
                  <option value="">Auto (based on niche)</option>
                  <optgroup label="Minimal">
                    {GRADIENT_PRESETS.minimal.map(p => (
                      <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Bold">
                    {GRADIENT_PRESETS.bold.map(p => (
                      <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Premium">
                    {GRADIENT_PRESETS.premium.map(p => (
                      <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Solid">
                    {GRADIENT_PRESETS.solid.map(p => (
                      <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Collection Banner Preset */}
              <div>
                <label className="block text-xs font-medium mb-2 text-zinc-300">Collection Banners</label>
                <select
                  value={collectionPreset}
                  onChange={e => setCollectionPreset(e.target.value)}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-sm"
                >
                  <option value="">Auto (based on niche)</option>
                  <optgroup label="Minimal">
                    {GRADIENT_PRESETS.minimal.map(p => (
                      <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Bold">
                    {GRADIENT_PRESETS.bold.map(p => (
                      <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Premium">
                    {GRADIENT_PRESETS.premium.map(p => (
                      <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* What will be generated */}
        <div className="mb-6 p-3 bg-zinc-900 rounded-lg">
          <h4 className="font-medium text-sm mb-2">What will be generated:</h4>
          <ul className="text-xs text-zinc-400 space-y-1">
            <li>• Brand identity (name, tagline, story)</li>
            <li>• About page with founder bio</li>
            <li>• FAQ page addressing real customer concerns</li>
            <li>• Shipping, Return, Privacy, Contact pages</li>
            <li>• Homepage hero, value props, testimonials</li>
            <li>• Trust badges & newsletter section</li>
            <li>• Collection pages with descriptions</li>
            <li>• Navigation menus & footer</li>
            <li>• SEO meta tags</li>
            {generateImages && <li className="text-cyan-400">• Store images (hero bg, logo, banners)</li>}
          </ul>
          <p className="text-xs text-zinc-500 mt-2">
            Estimated time: 2-4 minutes (depends on Ollama model speed)
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={loading || !selectedNiche || !ollamaStatus?.healthy}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Starting...
              </>
            ) : (
              'Start Provisioning'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProvisionModal;
